import React from "react";
import { Alert, Button, CircularProgress } from "@mui/material";
import { FaCheckCircle, FaExclamationTriangle, FaWifi } from "react-icons/fa";

// status: "saved" | "saving" | "failed" | "offline"

const formatClock = (ms) =>
  ms ? new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

// Small always-visible indicator for the header.
export const SaveStatusPill = ({ status, savedAt, onRetry }) => {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
    border: "1px solid",
  };
  if (status === "saving") {
    return (
      <span role="status" style={{ ...base, color: "var(--text-2, #6b7280)", borderColor: "var(--border-1, #d1d5db)" }}>
        <CircularProgress size={11} thickness={5} sx={{ color: "inherit" }} /> Saving…
      </span>
    );
  }
  if (status === "failed") {
    return (
      <button
        type="button"
        onClick={onRetry}
        style={{ ...base, color: "#b42318", borderColor: "#b42318", background: "rgba(218,20,20,0.08)", cursor: "pointer" }}
      >
        <FaExclamationTriangle size={11} /> Not saved · Retry
      </button>
    );
  }
  if (status === "offline") {
    return (
      <span role="status" style={{ ...base, color: "#b42318", borderColor: "#b42318", background: "rgba(218,20,20,0.08)" }}>
        <FaWifi size={11} /> Offline
      </span>
    );
  }
  return (
    <span role="status" style={{ ...base, color: "#287d3c", borderColor: "rgba(40,125,60,0.4)" }}>
      <FaCheckCircle size={11} /> Saved{savedAt ? ` ${formatClock(savedAt)}` : ""}
    </span>
  );
};

// Banner under the header when work is not reaching the server.
export const SaveProblemBanner = ({ status, error, isOnline, onRetry }) => {
  if (!isOnline) {
    return (
      <Alert severity="error" sx={{ mx: "20px", mb: 1.5 }}>
        <strong>You're offline.</strong> Tech QC needs an internet connection, so editing is paused. Your unsaved
        changes are kept on this computer and will be saved automatically when you're back online.
      </Alert>
    );
  }
  if (status !== "failed") return null;
  return (
    <Alert
      severity="error"
      sx={{ mx: "20px", mb: 1.5 }}
      action={
        <Button color="inherit" size="small" onClick={onRetry} sx={{ fontWeight: 700 }}>
          Retry now
        </Button>
      }
    >
      <strong>Your last change was not saved.</strong> The server didn't accept it after 3 tries
      {error ? ` (${error})` : ""}. Editing is paused until it's saved, so nothing else is lost. Your change is kept
      on this computer.
    </Alert>
  );
};

// Covers the event list while saving is blocked, so edits can't pile up unsaved.
export const EditingPausedOverlay = ({ isOnline, onRetry }) => (
  <div
    role="alert"
    style={{
      position: "absolute",
      inset: 0,
      zIndex: 5,
      display: "grid",
      placeItems: "center",
      background: "color-mix(in srgb, var(--surface-1, #fff) 82%, transparent)",
      backdropFilter: "blur(1px)",
      textAlign: "center",
      padding: 24,
    }}
  >
    <div style={{ maxWidth: 340, display: "grid", gap: 10, justifyItems: "center" }}>
      {isOnline ? <FaExclamationTriangle size={26} color="#b42318" /> : <FaWifi size={26} color="#b42318" />}
      <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-1, #111827)" }}>
        {isOnline ? "Editing paused: last change not saved" : "Editing paused: no internet"}
      </div>
      <div style={{ fontSize: 13, color: "var(--text-2, #6b7280)" }}>
        {isOnline
          ? "Retry the save to continue. Your change is kept on this computer."
          : "Reconnect to continue. Your changes will save automatically."}
      </div>
      {isOnline && (
        <Button variant="contained" size="small" onClick={onRetry} sx={{ textTransform: "none", bgcolor: "#ff5a1f", "&:hover": { bgcolor: "#e54d18" } }}>
          Retry save
        </Button>
      )}
    </div>
  </div>
);

// Shown once after load when a previous visit left changes that never saved.
export const RecoveryBanner = ({ savedAt, onRestore, onDiscard }) => (
  <Alert
    severity="warning"
    sx={{ mx: "20px", mb: 1.5 }}
    action={
      <>
        <Button color="inherit" size="small" onClick={onRestore} sx={{ fontWeight: 700 }}>
          Restore and save
        </Button>
        <Button color="inherit" size="small" onClick={onDiscard}>
          Discard
        </Button>
      </>
    }
  >
    <strong>Unsaved changes found.</strong> Changes from {savedAt ? new Date(savedAt).toLocaleString() : "an earlier visit"} never
    reached the server.
  </Alert>
);
